import React, { useEffect } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import AddIcon from "@mui/icons-material/Add";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import tokens from "@/theme/tokens";

import socialFacebook from "@/images/social_facebook.png";
import socialTwitter from "@/images/social_twitter.png";
import socialLinkedin from "@/images/social_linkedin.png";
import {
  editSocialLinks,
  saveSocialLinks,
  RESET_EDITING_SOCIAL_LINKS,
} from "@/store/profile/profileActions";
import {
  railFieldSx,
  railSectionSx,
  socialHandleSx,
  socialIconSx,
  socialLinkButtonSx,
  socialListSx,
} from "../styles";

interface SocialLinksProps {
  isUsersProfile: boolean;
}

type SocialLinkValues = {
  linkedin: string;
  twitter: string;
  facebook: string;
};

const NETWORKS = [
  { key: "linkedin", icon: socialLinkedin, label: "LinkedIn", url: "https://www.linkedin.com/in/" },
  { key: "twitter", icon: socialTwitter, label: "Twitter", url: "https://www.twitter.com/" },
  { key: "facebook", icon: socialFacebook, label: "Facebook", url: "https://www.facebook.com/" },
] as const;

const PLACEHOLDER_KEYS: Record<string, string> = {
  linkedin: "linkedinUsername",
  twitter: "twitterHandle",
  facebook: "facebookUsername",
};

/** The rail's section heading, with an optional action to its right. */
const RailHeading = ({
  title,
  action,
}: {
  title: string;
  action?: React.ReactNode;
}) => (
  <Stack
    direction="row"
    spacing={1}
    sx={{ alignItems: "center", justifyContent: "space-between", mb: 1 }}
  >
    <Typography variant="h3" sx={{ color: "common.white" }}>
      {title}
    </Typography>
    {action}
  </Stack>
);

const SocialLinks = ({ isUsersProfile }: SocialLinksProps) => {
  const { t } = useTranslation(["translation.Profile"]);
  const dispatch = useDispatch();

  const links = useSelector(
    (state: any) =>
      (isUsersProfile
        ? state.profile.socialLinks
        : state.browse.browseProfile.get("socialLinks")
      ).toJS() as Partial<SocialLinkValues>,
    shallowEqual
  );
  const socialLinksLoaded = useSelector((state: any) =>
    isUsersProfile
      ? Boolean(state.profile.pageMetaData.get("socialLinksLoaded"))
      : true
  );
  const isEditingSocialLinks = useSelector((state: any) =>
    isUsersProfile
      ? Boolean(state.profile.pageMetaData.get("isEditingSocialLinks"))
      : false
  );
  const isSavingSocialLinks = useSelector((state: any) =>
    isUsersProfile
      ? Boolean(state.profile.pageMetaData.get("isSavingSocialLinks"))
      : false
  );

  const hasSocialLinks = Boolean(
    links.twitter || links.facebook || links.linkedin
  );

  const { register, handleSubmit, reset } = useForm<SocialLinkValues>({
    defaultValues: {
      linkedin: links.linkedin || "",
      twitter: links.twitter || "",
      facebook: links.facebook || "",
    },
  });

  useEffect(() => {
    reset({
      linkedin: links.linkedin || "",
      twitter: links.twitter || "",
      facebook: links.facebook || "",
    });
  }, [links.linkedin, links.twitter, links.facebook, reset]);

  const edit = () => dispatch(editSocialLinks());
  const close = () => dispatch({ type: RESET_EDITING_SOCIAL_LINKS });
  const onSubmit = (values: SocialLinkValues) => dispatch(saveSocialLinks(values));
  const openSocialLink = (linkAddress: string, socialType: string) => {
    if (window.confirm(`Open in ${socialType}?`)) {
      window.open(linkAddress, "_blank");
    }
  };

  const editButton = (
    <IconButton
      aria-label={t("Edit")}
      onClick={edit}
      size="small"
      sx={{ color: tokens.color.accentTeal }}
    >
      <EditOutlinedIcon fontSize="small" />
    </IconButton>
  );

  if (!socialLinksLoaded) {
    return (
      <Box sx={railSectionSx}>
        <RailHeading title={t("SocialLinks")} />
        <Typography variant="body2" sx={{ color: "grey.400" }}>
          {t("loading")}
        </Typography>
      </Box>
    );
  }

  if (isEditingSocialLinks) {
    return (
      <Box sx={railSectionSx}>
        <RailHeading
          title={t("Connect")}
          action={
            <IconButton
              aria-label={t("Connect")}
              onClick={close}
              size="small"
              sx={{ color: "grey.400" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        />
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            {NETWORKS.map((network) => {
              // TextField's own ref goes to the root, so register()'s ref has
              // to be handed to the input it wraps.
              const { ref: registerRef, ...registerProps } = register(network.key);
              return (
                <Stack
                  key={network.key}
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: "center" }}
                >
                  <Box sx={socialIconSx}>
                    <img src={network.icon} alt={network.key} />
                  </Box>
                  <TextField
                    {...registerProps}
                    inputRef={registerRef}
                    size="small"
                    fullWidth
                    placeholder={t(PLACEHOLDER_KEYS[network.key])}
                    sx={railFieldSx}
                  />
                </Stack>
              );
            })}
            <Button
              type="submit"
              variant="contained"
              size="small"
              disableElevation
              disabled={isSavingSocialLinks}
              startIcon={
                isSavingSocialLinks ? (
                  <CircularProgress size={14} color="inherit" />
                ) : null
              }
              sx={{ alignSelf: "flex-end" }}
            >
              {t("save")}
            </Button>
          </Stack>
        </Box>
      </Box>
    );
  }

  if (hasSocialLinks) {
    return (
      <Box sx={railSectionSx}>
        <RailHeading
          title={t("Connect")}
          action={isUsersProfile ? editButton : undefined}
        />
        <Box sx={socialListSx}>
          {NETWORKS.filter((network) => links[network.key]).map((network) => (
            <Button
              key={network.key}
              onClick={() =>
                openSocialLink(
                  `${network.url}${links[network.key]}`,
                  network.label
                )
              }
              size="small"
              startIcon={
                <Box sx={socialIconSx}>
                  <img src={network.icon} alt={network.key} />
                </Box>
              }
              sx={socialLinkButtonSx}
            >
              <Box component="span" sx={socialHandleSx}>
                {links[network.key]}
              </Box>
            </Button>
          ))}
        </Box>
      </Box>
    );
  }

  if (!isUsersProfile) return null;

  return (
    <Box sx={railSectionSx}>
      <RailHeading title={t("SocialLinks")} action={editButton} />
      <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
        <Typography variant="body2" sx={{ color: "common.white" }}>
          {t("ProfilePhrase2")}
        </Typography>
        <Button
          onClick={edit}
          size="small"
          startIcon={<AddIcon fontSize="small" />}
          sx={{ ...socialLinkButtonSx, color: tokens.color.accentTeal }}
        >
          {t("AddSocialLinks")}
        </Button>
      </Stack>
    </Box>
  );
};

export default SocialLinks;
